import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import type { ColumnsType } from 'antd/es/table';
import { hooks, actions, selectors } from 'farmx-redux-core';
import {
  Button, notification, Typography, Tooltip
} from 'antd';
import { FaChevronLeft } from 'react-icons/fa';
//@ts-ignore
import { RanchBlockSelectMobile } from 'farmx-web-ui';
import {
  getReservoirConfigList, createReservoirConfig, updateReservoirConfig
} from '../../../helper/settingsHelper';
import EditableTable from './EditableTable';
import GlobalSettingsConfig from './GlobalSettingsConfig';
import { isMobile } from '../../../utils/detectDevice';
import Page404 from '../../app/Page404';
import './ruleSettingsPage.less';
import { GEC_ENTITY_ID, GEC_TEST_ENTITY_ID } from '../../../constants';
import isEqual from 'react-fast-compare';

const { 
  useEntities, useRanchBlockSelection, useSensorNames,
  useUnits, useRanchNamesForBlocks, useBlockNames
 } = hooks;

const {
  setRanchBlockSelection,
  setRanch, setBlocks,
} = actions;

const { selectLoginUserInfo, selectUserPressureFormat } = selectors;

const GlobalSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showControl, setShowControl] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [fetchList, setFetchList] = useState(true);
  const [configList, setConfigList] = useState([]);
  const [localBlocks, setLocalBlocks] = useState<any>([]);
  const [selected, setSelected] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(false);
  const { activeEntityId } = useEntities();
  const userInfo = (useSelector(selectLoginUserInfo) as {payload:{admin:boolean}}).payload;
  const formRef = useRef<any>(null);
  const { selectedObjFromState } = useRanchBlockSelection();
  const pressureFormat = useSelector(selectUserPressureFormat);
  const fromUnit = 'pounds_per_square_inch';
  const getUserUnits = useUnits();
  const pressureLabel = pressureFormat === 'pounds_per_square_inch' ? 'psi' : 'mpa';
  const blockNames = useBlockNames(localBlocks) || {};
  const ranchNames = useRanchNamesForBlocks(localBlocks) || {};

  moment.locale('en');
  const sensorNames = useSensorNames(activeEntityId);

  function updateRespone(response: any) {
    return response?.map((obj: any) => {
      const { reservoir_threshold: reservoirThreshold, id } = obj || {};
      const { blocks, pressure_low_threshold, pressure_high_threshold,
        pressure_reference_sensor_type: type,
        pressure_reference_sensor_identifier: identifier,
        } = reservoirThreshold || {};
      return ({
      ...obj,
      blockCount: blocks?.length,
      key: `${obj?.updated_at}_${id}`,
      pressure_low_threshold,
      pressure_high_threshold,
      sensor_name: sensorNames?.[`${type}/${identifier}`] || 'No Name',
      updated_at: moment(obj?.updated_at).format('YYYY-MM-DD HH:mm:ss'),
    })
    }) || [];
  }

  useEffect(() => {
    if (!fetchList) return;

    getReservoirConfigList((success: boolean, response: any) => {
      if (success) {
        const updatedResponse = updateRespone(response?.data);
        console.log('updatedResponse :: ', updatedResponse, response?.data)
        if (!isEqual(configList, updatedResponse)) {
          setConfigList(updatedResponse);
        }
        setFetchList(false);
      } else {
        notification.error({
          message: t('Internal Server Error while fetching data!'),
        });
      }
    });
  }, [fetchList]);

  // To set the all config blocks to state
  useEffect(() => {
    if (configList?.length) {
      const blocks = configList.map((d) => d?.reservoir_threshold?.blocks).flat();
      if (!isEqual(blocks, localBlocks)) {
        setLocalBlocks(blocks);
      }
    }
  }, [localBlocks, configList])

  const getTooltipContent = (blockIds) => {
    return (
      <div className="custom-tooltip-content">
        {blockIds.map((blkId) => <div>
          {`${blockNames?.[blkId]} (${ranchNames?.[blkId]})`}
        </div>)}
      </div>
    )
  }

  // Show 404:Page when GEC not selected
  if ((activeEntityId !== GEC_ENTITY_ID && activeEntityId !== GEC_TEST_ENTITY_ID)) return <Page404 />;

  // The columns will be updated in the future
  const columns: ColumnsType = [
    {
      title: t('Main Pressure Sensor'),
      dataIndex: 'sensor_name',
      key: 'sensor_name',
      width: 200
    },
    {
      title: t('Blocks'),
      dataIndex: 'blockCount',
      key: 'blockCount',
      render: (_, record: any) => {
        return (
          <Tooltip
          title={getTooltipContent(record?.reservoir_threshold?.blocks)}
          overlayClassName="custom-tooltip"
          >
            {record.blockCount}
          </Tooltip>
        )
      },
      width: 100
    },
    {
      title: `${t('Stopping Cut off')} (${pressureLabel})`,
      dataIndex: 'pressure_low_threshold',
      key: 'pressure_low_threshold',
      render: (_, record: any) => {
        const {value: pressureLow } = getUserUnits(Number(record?.pressure_low_threshold || 0),
        fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
        return Number(pressureLow);
      },
      width: 200
    },
    {
      title: `${t('Resuming Cut off')} (${pressureLabel})`,
      dataIndex: 'pressure_high_threshold',
      key: 'pressure_high_threshold',
      render: (_, record: any) => {
        const {value: pressureHigh } = getUserUnits(Number(record?.pressure_high_threshold || 0),
        fromUnit, undefined, { decimalPlaces: 3 }, pressureFormat);
        return Number(pressureHigh);
      },
      width: 200
    },
    {
      title: t('Last Updated At'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 200
    }
  ];

  const onGoBack = () => {
    if (showControl) setShowControl(false);
    if (isEdit) setIsEdit(false);
    if (selected) setSelected(undefined);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (formRef.current) formRef?.current?.resetFields();
  };

  const onEdit = (value: any) => {
    setIsEdit(true);
    setSelected(value);
  };

  const handleFormRef = (value: any) => { formRef.current = value; };

  const handleUpdate = (obj: any) => {
    setLoading(true);
    if (obj?.create) {
      createReservoirConfig(obj?.create, (success: boolean, response: any) => {
        if (success) {
          notification.success({
            message: `${t('Created Successfully')}!`,
          });
          setFetchList(true);
          onGoBack();
        } else {
          notification.error({
            message: t('Failed to create'),
            description: response || t('Something went wrong'),
          });
        }
        setLoading(false);
      });
    }
    if (obj?.edit) {
      const { id } = obj?.edit || {};
      const updateObj = { config: obj?.edit, configId: id };
      updateReservoirConfig(updateObj, (success: boolean, response: any) => {
        if (success) {
          notification.success({
            message: `${t('Edited Successfully')}!`,
          });
          setFetchList(true);
          onGoBack();
        } else {
          notification.error({
            message: t('Failed to edit'),
            description: response || t('Something went wrong'),
          });
        }
        setLoading(false);
      });
    }
    if (obj?.delete) {
      setFetchList(true);
      onGoBack();
      setLoading(false);
    }
  };

  function handleSelect(d: any) {
    if (d && d.value !== undefined) {
      dispatch(setRanchBlockSelection(d));
      if (d?.type === 'block' && !isMobile) dispatch(setBlocks([Number(d?.value)]));
      if (d?.type === 'ranch' && !isMobile) dispatch(setRanch(Number(d?.value)));
    }
  }

  const title = isEdit ? `${t('Update')} / ${t('Delete Reservoir Fail Safe Settings')}` : t('Create Reservoir Fail Safe Settings');

  return (
    <div className={`single-column-page padded desktop-container desktop-container-config
     ${(showControl || isEdit)? 'background-white height-max' : ''}`}
    >
      {(!showControl && !isEdit) && (
        <>
          <div className="flex-space-between">
            <div>
              <Typography.Title level={3}>{t('Reservoir Fail Safe Settings')}</Typography.Title>
            </div>
            <div className="btn-align-end">
              <Button type="primary" htmlType="submit" onClick={() => setShowControl(true)}>
                {t('Create')}
              </Button>
            </div>
          </div>
          <div className="div-select-container ranch-block-select-container">
            {isMobile && (!showControl && !isEdit) && (
            <RanchBlockSelectMobile
              onSelect={handleSelect}
              selected={selectedObjFromState}
              admin={userInfo && userInfo.admin}
            />
            )}
          </div>
          <EditableTable
            data={configList}
            columns={columns}
            onEdit={(d: any) => onEdit(d)}
            loadingId={loadingId}
            showIsActive={false}
          />
        </>
      )}
      {(showControl || isEdit) && (
        <>
          <div className="flex-space-between btn-align-start">
            <div className={`${(isMobile && title?.length > 50)
              ? 'page-header-length' : `page-header ${!isMobile ? 'custom-width' : ''}`}`}
            >
              <div className="page-header-main back-header">
                <Button
                  id="goBack"
                  className="back-btn"
                  shape="circle"
                  size="large"
                  icon={<FaChevronLeft size={20} />}
                  onClick={() => onGoBack()}
                />
                <div className="rule-setting-form-title margin-left-0">
                  <Typography.Title level={1}>{title}</Typography.Title>
                </div>
              </div>
            </div>
          </div>
          <GlobalSettingsConfig
            key={`config-${activeEntityId}`}
            isEdit={isEdit}
            settings={selected}
            loading={loading}
            formRef={handleFormRef}
            onCancel={onGoBack}
            onUpdate={handleUpdate}
          />
        </>
      )}
    </div>
  );
};

export default GlobalSettingsPage;
